<script lang="ts">
export default {
  name: 'ReviewSummaryTable',
};
</script>

<script setup lang="ts">
import type { Component } from 'vue';
import { ArrowLeft, FileText, Folders, LayoutGrid, RefreshCw, Shield } from '@lucide/vue';
import AppButton from '@/components/v2/AppButton.vue';
import Skeleton from '@/components/v2/Skeleton.vue';

export interface ReviewSummaryItem {
  key: string;
  label: string;
  count: number | null;
  // Optional muted line under the label (e.g. "Corrections sync overnight from Epic").
  caption?: string;
}

defineProps<{
  items: ReviewSummaryItem[];
  loading: boolean;
}>();

const emit = defineEmits<{
  review: [index: number];
}>();

const STEP_ICONS: Record<string, Component> = {
  binders: FileText,
  policies: FileText,
  epic: RefreshCw,
  demographics: LayoutGrid,
  policyDetails: Shield,
  programGrouping: Folders,
};
</script>

<template>
  <div class="overflow-x-auto rounded-md border border-base-300">
    <table class="table table-sm">
      <thead>
        <tr class="bg-base-200 text-xs tracking-wide text-base-content/60 uppercase">
          <th>Step</th>
          <th>Issue count</th>
          <th>Status</th>
          <th class="text-right">Action</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(item, index) in items"
          :key="item.key">
          <td>
            <div class="flex items-center gap-3">
              <div
                v-if="STEP_ICONS[item.key]"
                class="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-base-300 bg-base-100 text-base-content/60">
                <component
                  :is="STEP_ICONS[item.key]"
                  :size="15" />
              </div>
              <div class="min-w-0">
                <div class="font-medium">{{ item.label }}</div>
                <div
                  v-if="item.caption"
                  class="text-xs text-base-content/50">
                  {{ item.caption }}
                </div>
              </div>
            </div>
          </td>
          <template v-if="loading || item.count == null">
            <td><Skeleton size="sm" /></td>
            <td><Skeleton width="6rem" height="1.25rem" /></td>
          </template>
          <template v-else>
            <td>{{ item.count }}</td>
            <td>
              <span
                v-if="item.count === 0"
                class="badge badge-soft badge-sm badge-success whitespace-nowrap">
                All clear
              </span>
              <span
                v-else
                class="badge badge-soft badge-sm badge-error whitespace-nowrap">
                {{ item.count }} issue{{ item.count === 1 ? '' : 's' }}
              </span>
            </td>
          </template>
          <td class="text-right">
            <AppButton
              outline
              size="xs"
              :data-testid="`review-step-${item.key}`"
              @click="emit('review', index)">
              <ArrowLeft :size="14" />
              Review
            </AppButton>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
